import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioButton,
} from '../../components/BrioComponents';
import { formatSeriesRange } from '../utils/itemCodeGenerator';

// Copy a library item to a new code. The copy keeps the source's trade group
// and chapter / sub-trade; the trade type is chosen here (preset to the
// source's own), and the code is the next number in that type's series. A
// sub-trade with no trade types numbers from its own range, and the picker is
// then disabled.
//
// What is copied: every live version (letters kept), or one chosen version,
// which becomes the copy's base version. No rate is carried over (see
// duplicateItem in useBoqLibrary).
//
// `generateCode(tradeTypeNodeId)` returns { code } or { error } from
// nextDuplicateCode, so the code box follows the picker.
function DuplicateItemModal({ source, tradeTypes = [], subtradeNodeId, sourceTradeTypeId, generateCode, onDuplicate, onClose }) {
  const typesHere = useMemo(
    () => tradeTypes.filter((t) => t.anchorId && String(t.anchorId) === String(subtradeNodeId)),
    [tradeTypes, subtradeNodeId]
  );
  const hasTypes = typesHere.length > 0;
  const [tradeTypeId, setTradeTypeId] = useState(
    typesHere.some((t) => t.id === sourceTradeTypeId) ? sourceTradeTypeId : ''
  );
  const generated = generateCode(hasTypes ? tradeTypeId : undefined);
  const itemCode = generated.code || '';

  const versions = source.versions || [];
  const liveCount = versions.filter((v) => v.status !== 'deprecated').length;
  const [scope, setScope] = useState('all');          // 'all' | 'one'
  const [versionId, setVersionId] = useState(
    (versions.find((v) => v.status === 'active') || versions[0])?.id || ''
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!itemCode) return;
    setError(null);
    setSubmitting(true);
    const res = await onDuplicate({
      sourceItemCode: source.item.itemCode,
      itemCode,
      versionId: scope === 'one' ? versionId : null,
      // Only override the source's tag when there are types to choose from.
      ...(hasTypes ? { tradeTypeNodeId: tradeTypeId || null } : {}),
    });
    setSubmitting(false);
    if (res.success) onClose(res);
    else setError(res.error || 'Could not duplicate this item.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting || !itemCode || (scope === 'one' && !versionId)}>
        {submitting ? 'Duplicating…' : 'Duplicate item'}
      </BrioButton>
    </>
  );

  const radioRow = { display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.85rem', cursor: 'pointer' };

  return (
    <BrioModal title="Duplicate library item" onClose={onClose} width="md" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--brio-text-muted, #6b7280)' }}>
          Copying <code style={{ fontWeight: 600 }}>{source.item.itemCode}</code>
          {source.description ? ` — ${source.description}` : ''}
        </div>
        <form onSubmit={handleSubmit}>
          <BrioModalField
            label="Trade type"
            hint={hasTypes
              ? 'Supplies the item number. Listed: the trade types under the source item\'s chapter / sub-trade.'
              : 'This sub-trade has no trade types, so the copy is numbered from the sub-trade\'s own range.'}
          >
            <BrioSelect value={tradeTypeId} onChange={(e) => setTradeTypeId(e.target.value)} disabled={submitting || !hasTypes}>
              <option value="">{hasTypes ? '— Select trade type —' : '— None under this sub-trade —'}</option>
              {typesHere.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}{t.seriesStart != null ? ` · ${formatSeriesRange(t.seriesStart, t.seriesEnd)}` : ' · no series set'}
                </option>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField
            label="New item code"
            hint="Generated from the source item's trade group and chapter / sub-trade and the trade type above."
          >
            <BrioInput
              value={itemCode}
              readOnly
              placeholder="—"
              style={{
                fontFamily: 'monospace',
                background: 'var(--brio-bg-subtle, #f7f7f5)',
              }}
            />
          </BrioModalField>
          {generated.error && (
            <div style={{ padding: '0.5rem 0.75rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem', fontSize: '0.84rem' }}>
              {generated.error}
            </div>
          )}

          {versions.length > 1 && (
            <BrioModalField label="Versions to copy">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={radioRow}>
                  <input type="radio" name="dup-scope" checked={scope === 'all'} onChange={() => setScope('all')} disabled={submitting} />
                  All live versions ({liveCount}), letters kept
                </label>
                <label style={radioRow}>
                  <input type="radio" name="dup-scope" checked={scope === 'one'} onChange={() => setScope('one')} disabled={submitting} />
                  One version only
                </label>
                {scope === 'one' && (
                  <BrioSelect value={versionId} onChange={(e) => setVersionId(e.target.value)} disabled={submitting}>
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.fullCode}{v.status === 'deprecated' ? ' (archived)' : v.status === 'draft' ? ' (candidate)' : ''} · {v.shortDescription}
                      </option>
                    ))}
                  </BrioSelect>
                )}
              </div>
            </BrioModalField>
          )}

          <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted, #6b7280)', lineHeight: 1.5 }}>
            {scope === 'one'
              ? <>The chosen version becomes the copy&apos;s base version and its active version, with its description, unit, measurement reference, three-part spec and classification. </>
              : <>The copy carries the description, unit, measurement reference, three-part spec and classification of every live version of the source. Archived versions stay with the original. </>}
            <strong>No rates are linked</strong>: the copy starts with an empty rate history.
          </div>
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default DuplicateItemModal;
